<template>
  <div
    v-if="entry && (accountDescription || showPlayBrief)"
    class="briefCards"
  >
    <div v-if="accountDescription" class="brief">
      <span class="briefIcon"><AppIcon name="account_outline" :size="16" /></span>
      <div>
        <div class="briefLabel">About {{ entry.account_name || 'account' }}</div>
        <p class="briefText">{{ accountDescription }}</p>
      </div>
    </div>
    <div v-if="showPlayBrief" class="brief">
      <span class="briefIcon" :style="{ color: groupColor }"><AppIcon name="list" :size="16" /></span>
      <div>
        <div class="briefLabel">About {{ entry.sales_play_name || 'sales play' }}</div>
        <p class="briefText">{{ entry.sales_play_description }}</p>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import AppIcon from '../../../components/AppIcon.vue'
import { apiFetch } from '../../../services/api'
import type { CallerQueueEntry } from '../../../services/dialerApi'
import { useCurrentUser } from '../../../services/user'

// Background context — the "About <account>" / "About <sales play>" briefs.
// Shared by the pre-dial focus card and the live call card.
const props = defineProps<{
  entry: CallerQueueEntry | null
  groupColor: string
}>()

// Dialer-only tenants have no sales plays; never surface one to them.
const { isDialerOnly } = useCurrentUser()
const showPlayBrief = computed(() => !!props.entry?.sales_play_description && !isDialerOnly.value)

const accountDescription = ref('')

watch(
  () => props.entry?.account_id,
  async (accountId) => {
    accountDescription.value = ''
    if (!accountId) return
    const requested = accountId
    try {
      const resp = await apiFetch(`/accounts/${encodeURIComponent(accountId)}/fields/leadiq`)
      if (props.entry?.account_id !== requested) return
      const fields = Array.isArray(resp?.fields) ? resp.fields : []
      accountDescription.value = fields.find((f: any) => f.key === 'description')?.value || ''
    } catch {
    }
  },
  { immediate: true },
)
</script>

<style scoped>
.briefCards { display: grid; grid-template-columns: 1fr 1fr; gap: var(--space-3); }
/* BACKGROUND-region content recedes on purpose: the page's own beige (the same
   ground the person block sits on — one beige, never two) with no shadow, so
   the white actionable cards alternate against it and figure/ground matches
   the panel's why → who → call → background rhythm. */
.brief { display: flex; gap: var(--space-3); padding: var(--space-4); background: var(--color-bg-app); border: 1px solid var(--color-border-subtle); border-radius: var(--radius-lg); }
.briefIcon { display: inline-flex; align-items: center; justify-content: center; width: 28px; height: 28px; border-radius: var(--radius-md); background: var(--color-bg-subtle); flex-shrink: 0; }
.briefLabel { font-size: var(--fs-xs); font-weight: var(--fw-semibold); letter-spacing: var(--tracking-wide); text-transform: uppercase; color: var(--color-text-tertiary); }
.briefText { margin-top: 4px; font-size: var(--fs-sm); line-height: var(--lh-normal); color: var(--color-text-secondary); }

/* ── ADAPTIVE ── */
@container (max-width: 560px) {
  .briefCards { grid-template-columns: 1fr; }
}
</style>
